import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import {
    AfterViewChecked,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    QueryList,
    SimpleChanges,
    ViewChild,
    ViewChildren,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import {
    AumicoClipboardService,
    AumicoCoreProvider,
    EMPTY_ARRAY_LENGTH,
    ModalComponent,
    ModalData,
    ModalTitleIcons,
    NotificationType,
} from '@modeso/aumico-lib-core-fe';
import { AumicoClipboardData } from '@modeso/aumico-lib-core-fe/src/lib/interfaces/aumico-clipboard-data.interface';
import {
    IAccountMappingTemplate,
    IEquity,
    IFinancialLineItemComponent,
    IFreeTable,
    IMovementScheduleComponent,
    IMovementScheduleFli,
    INotesComponent,
    INotesInputComponent,
    INotesSelectComponent,
    ISuperTableComponent,
    Notes,
} from '@modeso/types__aumico-lib-annualreport-be';
import { ViewMode } from '@modeso/types__aumico-lib-core-be';
import { ComponentType, IRichText } from '@modeso/types__aumico-lib-notes-be';
import { TemplateType } from '@modeso/types__aumico-lib-templates-be';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import Debug from 'debug';
import { cloneDeep, omit } from 'lodash-es';
import { Subject, filter } from 'rxjs';
import { NotesActionsPaletteComponent } from '../../../../../aumico-components/src/lib/components/notes-actions-pallete/notes-actions-palette.component';
import { ViewState } from '../../enums/ViewState.enum';
import { AccordionCompletenessHelper } from '../../helpers/accordionCompleteness.helper';
import { MovementScheduleHelper } from '../../helpers/movementSchedule.helper';
import { NotesHelper } from '../../helpers/notes.helper';
import { getAllFlis } from '../../helpers/notesCopy.helper';
import { IAnnualReportDefaultSettings } from '../../interfaces/IAnnualReportDefaultSettings.interface';
import { FxCurrencyDisplay, IFxNodeStamp, IFxNotesContext, effectiveFxCurrencyDisplay } from '../../interfaces/IFxNotesContext.interface';
import { IFLIForNotes } from '../../interfaces/IFLIForNotes.interface';
import { MappingItemsSelectionNode } from '../../interfaces/mapping-items-selection-node.interface';
import { INotesComponentsNumberedTitles } from '../../interfaces/notes-numbered-titles.interface';
import { ISuperTableItemSelection } from '../../interfaces/super-table-item-selection.interface';
import { SuperTableMapper } from '../../mappers/super-table.mapper';
import { NoteComponentViewState } from '../../types/NoteComponentViewState.types';
import { IEquityDelegate } from '../equity/IEquityDelegate.interface';
import { IFinancialLineItemDelegate } from '../fli/IFliDelegate.component';
import { IFreeTableDelegate } from '../freeTableWithAmounts/IFreeTableDelegate.interface';
import {
    ReferencingNotesComponent,
    ReferencingNotesComponentData,
} from '../referencing-notes/referencing-notes.component';
import { IRichTextDelegate } from '../richText/IRichTextDelegate.interface';
import { ISelectDelegate } from '../select/ISelectDelegate.interface';
import { INotesComponentsListDelegation } from './INotesComponentsListDelegation.interface';

const debug = Debug('aumico:aumico-lib-notes-fe:NotesComponentsListComponent');

const MAX_SUPER_TABLE_COLUMN_NUMBER = 7;
const MAX_SUPER_TABLE_ROW_NUMBER = 7;

@UntilDestroy()
@Component({
    selector: 'aumico-notes-components-list',
    templateUrl: 'notes-components-list.component.html',
    styleUrls: ['notes-components-list.component.scss'],
})
export class NotesComponentsListComponent
    implements
    OnInit,
    OnChanges,
    IEquityDelegate,
    IFinancialLineItemDelegate,
    IFreeTableDelegate,
    IRichTextDelegate,
    ISelectDelegate,
    AfterViewChecked {
    @ViewChild('menuFlyout') menuFlyout!: ElementRef;

    @Input() reportLocale: string;
    @Input() templateFlis: string;

    @Input() languages: string[] = ['de-ch', 'en-us'];
    @Input() defaultLanguage = 'de-ch';

    resetState: boolean[];

    menuPosition = { top: 0, right: 0 };
    isMenuOpenIndex: number | null = null;
    private positionCalculated = false;
    componentIndexToInsertAfter: number | undefined;

    private _componentData: NoteComponentViewState[];
    componentIndexToBeDeleted: number;
    showComponentDeletionModal: boolean;
    showCurrentYearDeletionDialog: boolean;
    showCompareYearsDeletionDialog: boolean;
    selectedComponentForCompareYearsDeletion: NoteComponentViewState;
    selectedComponentForCurrentYearDeletion: NoteComponentViewState;
    componentIndexForCompareYearsDeletion: number;
    componentIndexForCurrentYearDeletion: number;
    @Input()
    get componentData(): NoteComponentViewState[] {
        return this._componentData;
    }

    set componentData(value: NoteComponentViewState[]) {
        this._componentData = cloneDeep<NoteComponentViewState[]>(value);
        this.resetState = Array(this.componentData.length).fill(false);
        this.initializeDataToBeSaved(value);
        this.isComponentDataReady = true;
    }
    state = ViewState;
    viewModeEnum = ViewMode;
    dataToBeSaved: Notes[] = [];
    formStatus: boolean[] = [];
    isComponentDataReady = false;

    @Input() public set flis(value: IFLIForNotes[]) {
        this.flisWithAccounts = value;
        this.movementScheduleFlis = this.movementScheduleHelper.prepareMovementScheduleFlis(this.flisWithAccounts);
    }

    private _balanceMappingTemplate: IAccountMappingTemplate | undefined;
    private _rawBalanceMappingTemplate: IAccountMappingTemplate | undefined;
    balanceMappingItemsSelectionTree: MappingItemsSelectionNode | undefined;
    @Input() public set balanceMappingTemplate(value: IAccountMappingTemplate) {
        if (value != null) {
            this._balanceMappingTemplate = cloneDeep<IAccountMappingTemplate>(value);
            // Only build the selection tree from the transformed template if no raw
            // template has been supplied — raw wins (REV-21023). If raw arrives later
            // its setter overwrites the tree.
            if (this._rawBalanceMappingTemplate == null) {
                this.balanceMappingItemsSelectionTree = cloneDeep<MappingItemsSelectionNode>(
                    this.superTableMapper.buildMappingItemsSelectionTree(value.group, TemplateType.BALANCE, this.fxNotesContext),
                );
            }
        }
    }

    // Raw (pre-G&M) template — when provided, drives the SuperTable selection tree
    // so account picking ignores Grouping/Masking (REV-21023).
    @Input() public set rawBalanceMappingTemplate(value: IAccountMappingTemplate) {
        if (value != null) {
            this._rawBalanceMappingTemplate = cloneDeep<IAccountMappingTemplate>(value);
            this.balanceMappingItemsSelectionTree = cloneDeep<MappingItemsSelectionNode>(
                this.superTableMapper.buildMappingItemsSelectionTree(value.group, TemplateType.BALANCE, this.fxNotesContext),
            );
        }
    }

    private _incomeMappingTemplate: IAccountMappingTemplate | undefined;
    private _rawIncomeMappingTemplate: IAccountMappingTemplate | undefined;
    incomeMappingItemsSelectionTree: MappingItemsSelectionNode | undefined;
    @Input() public set incomeMappingTemplate(value: IAccountMappingTemplate) {
        if (value != null) {
            this._incomeMappingTemplate = cloneDeep<IAccountMappingTemplate>(value);
            if (this._rawIncomeMappingTemplate == null) {
                this.incomeMappingItemsSelectionTree = cloneDeep<MappingItemsSelectionNode>(
                    this.superTableMapper.buildMappingItemsSelectionTree(value.group, TemplateType.INCOME),
                );
            }
        }
    }

    @Input() public set rawIncomeMappingTemplate(value: IAccountMappingTemplate) {
        if (value != null) {
            this._rawIncomeMappingTemplate = cloneDeep<IAccountMappingTemplate>(value);
            this.incomeMappingItemsSelectionTree = cloneDeep<MappingItemsSelectionNode>(
                this.superTableMapper.buildMappingItemsSelectionTree(value.group, TemplateType.INCOME),
            );
        }
    }

    @Input() public set index(value: number) {
        this.accordionIndex = value;
    }

    private _notesNumberedTitles: INotesComponentsNumberedTitles[] = [];

    @Input()
    set notesNumberedTitles(value: INotesComponentsNumberedTitles[] | undefined) {
        if (value != null) {
            this._notesNumberedTitles = value;
        }
    }

    get notesNumberedTitles(): INotesComponentsNumberedTitles[] {
        return this._notesNumberedTitles;
    }

    DELEGATE?: INotesComponentsListDelegation;

    @Input() public set delegate(delegate: INotesComponentsListDelegation) {
        if (delegate) {
            this.DELEGATE = delegate;
        }
    }

    @Input() viewMode: ViewMode = ViewMode.EDIT;

    @Input() annualReportDefaultSettings: IAnnualReportDefaultSettings | undefined;
    /** FX (REV-21512): null on non-FX reports — every component then behaves exactly as today. */
    @Input() fxNotesContext: IFxNotesContext | null = null;
    /** FX (REV-21512): per-account display values for the FLI tables, keyed `id|accountName`. */
    @Input() fliFxStamps: Map<string, IFxNodeStamp> | null = null;

    @Input() richTextFromInformationUpdate$ = new Subject<{
        informationRichText: INotesComponent;
        componentIndex: number;
        accordionIndex: number;
    }>();

    private _highlightTodos = false;
    @Input()
    get highlightTodos(): boolean {
        return this._highlightTodos;
    }

    set highlightTodos(value: boolean) {
        this._highlightTodos = value;
    }

    @Input() autofillMode = false;

    @Output() onComponentDataChange: EventEmitter<{ data: INotesComponent; componentIndex: number }> =
        new EventEmitter();

    @Output() onDeleteComponent: EventEmitter<number> = new EventEmitter();
    @Output() onDropComponents: EventEmitter<{ previousIndex: number; currentIndex: number }> = new EventEmitter();
    @Output() onComponentReferenceChange: EventEmitter<{
        data: Pick<INotesComponent, 'addNumbering' | 'referenceFlis'>;
        componentIndex: number;
    }> = new EventEmitter();
    @Output() onTableReferenceChange: EventEmitter<{
        data: Pick<Notes, 'addNumbering' | 'referenceFlis'>;
        componentIndex: number;
        tableIndex?: number;
    }> = new EventEmitter();
    @Output() onFliComponentOrderChanged: EventEmitter<{
        previousIndex: number;
        currentIndex: number;
        componentIndex: number;
    }> = new EventEmitter();

    accordionIndex: number;
    flisWithAccounts: IFLIForNotes[] = [];
    movementScheduleFlis: IMovementScheduleFli[] = [];
    balanceMappingSuperTableItems: ISuperTableItemSelection[] = [];
    incomeMappingSuperTableItems: ISuperTableItemSelection[] = [];

    @Input() set resetValuesFromMappingForAllComponents(value: boolean) {
        if (!value) {
            return;
        }
        this.resetState = Array(this.componentData.length).fill(true);
        setTimeout(() => (this.resetState = Array(this.componentData.length).fill(false)), 0);
        this.recheckReferencesExistenceForAllComponents();
    }
    componentNames = ComponentType;

    public isEditIconClicked = false;
    public isSaveIconClicked = false;

    @Input() supportOperation = false;
    @ViewChildren('dynamicComponent') dynamicComponents!: QueryList<any>;
    @ViewChildren('dynamicNotesActionsPalette') dynamicNotesActionsPalette!: QueryList<NotesActionsPaletteComponent>;

    componentsTitlesMap: Map<string, string> = new Map<string, string>();

    constructor(
        private readonly movementScheduleHelper: MovementScheduleHelper,
        private readonly notesHelper: NotesHelper,
        private readonly matIconRegistry: MatIconRegistry,
        private readonly domSanitizer: DomSanitizer,
        private readonly dialog: MatDialog,
        private readonly accordionCompletenessHelper: AccordionCompletenessHelper,
        private readonly superTableMapper: SuperTableMapper,
        private readonly aumicoClipboardService: AumicoClipboardService,
        private readonly aumicoCoreProvider: AumicoCoreProvider,
    ) {
        this.addSvgIcons();
    }

    ngOnInit(): void {
        this.richTextFromInformationUpdate$
            .pipe(
                filter((data) => data.accordionIndex === this.accordionIndex),
                untilDestroyed(this),
            )
            .subscribe((data) => {
                if (this.componentData[data.componentIndex] != null) {
                    this.componentData[data.componentIndex] = data.informationRichText;
                    this.dataToBeSaved[data.componentIndex] = data.informationRichText.data;
                } else {
                    this.componentData.push(data.informationRichText);
                }
            });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['supportOperation']) {
            this.positionCalculated = false;
        }
    }

    getMovementScheduleComponentData(componentData: IMovementScheduleComponent, componentIndex: number): void {
        if (this.componentData[componentIndex] != null) {
            this.dataToBeSaved[componentIndex] = componentData;
            (this.dataToBeSaved[componentIndex] as IMovementScheduleComponent).headerOptions =
                componentData.flis[0]?.headers;

            const updatedComponent: INotesComponent = {
                ...this.componentData[componentIndex],
                data: componentData,
            };

            this.onComponentDataChange.emit({ data: updatedComponent, componentIndex });
        }
    }

    richTextContent(content: IRichText[], componentIndex: number) {
        debug('rich text content');
        debug(content);

        if (this.componentData[componentIndex] != null) {
            this.dataToBeSaved[componentIndex] = content;
            const updatedComponent: INotesComponent = {
                ...this.componentData[componentIndex],
                data: content,
            };
            this.onComponentDataChange.emit({ data: updatedComponent, componentIndex });
        }
    }

    equityComponentData(equityComponentData: IEquity, componentIndex: number) {
        debug('equityComponentData');
        debug(equityComponentData);

        if (this.componentData[componentIndex] != null) {
            this.dataToBeSaved[componentIndex] = equityComponentData;
            const updatedComponent: INotesComponent = {
                ...this.componentData[componentIndex],
                data: equityComponentData,
            };

            this.onComponentDataChange.emit({ data: updatedComponent, componentIndex });
        }
    }

    fliComponentData(fliComponentData: IFinancialLineItemComponent, componentIndex: number) {
        debug('fliComponentData', fliComponentData);
        if (this.componentData[componentIndex] != null) {
            this.dataToBeSaved[componentIndex] = fliComponentData;
            const updatedComponent: INotesComponent = {
                ...this.componentData[componentIndex],
                data: fliComponentData,
            };

            this.onComponentDataChange.emit({ data: updatedComponent, componentIndex });
        }
    }

    freeTableComponentData(freeTableComponentData: IFreeTable, componentIndex: number) {
        debug('freeTableComponentData', freeTableComponentData);

        if (!freeTableComponentData) return;

        if (this.componentData[componentIndex] != null) {
            (this.componentData[componentIndex].data as IFreeTable).options = freeTableComponentData.options;

            this.dataToBeSaved[componentIndex] = {
                ...freeTableComponentData,
            };

            const updatedComponent: INotesComponent = {
                ...this.componentData[componentIndex],
                data: freeTableComponentData,
            };

            this.onComponentDataChange.emit({ data: updatedComponent, componentIndex });
        }
    }

    selectComponentData(selectData: INotesSelectComponent, componentIndex: number) {
        if (this.componentData[componentIndex] != null) {
            (this.componentData[componentIndex].data as INotesSelectComponent).options = selectData.options;

            if (this.componentData[componentIndex].data.options?.settings === undefined) {
                this.componentData[componentIndex].data.options.settings = false;
            }

            this.componentData[componentIndex].data = selectData;
            this.dataToBeSaved[componentIndex] = selectData;
            const updatedComponent: INotesComponent = {
                ...this.componentData[componentIndex],
                data: cloneDeep<INotesSelectComponent>(selectData),
            };

            this.onComponentDataChange.emit({ data: updatedComponent, componentIndex });
        }
    }

    inputComponentData(inputData: INotesInputComponent, componentIndex: number) {
        debug('delegate function inputComponentData called', inputData);
        if (this.componentData[componentIndex] != null) {
            this.dataToBeSaved[componentIndex] = inputData;
            (this.componentData[componentIndex].data as INotesInputComponent).options = inputData.options;
            const updatedComponent: INotesComponent = {
                ...this.componentData[componentIndex],
                data: inputData,
            };

            this.onComponentDataChange.emit({ data: updatedComponent, componentIndex });
        }
    }

    superTableComponentData(superTableData: ISuperTableComponent, componentIndex: number) {
        debug(' delegate function superTableComponentData called', superTableData);
        debug(superTableData);

        if (this.componentData[componentIndex] != null) {
            this.dataToBeSaved[componentIndex] = superTableData;
            (this.componentData[componentIndex].data as ISuperTableComponent).options = superTableData.options;
            const updatedComponent: INotesComponent = {
                ...this.componentData[componentIndex],
                data: superTableData,
            };

            this.onComponentDataChange.emit({ data: updatedComponent, componentIndex });
        }
    }

    onDelete(index: number): void {
        this.componentIndexToBeDeleted = index;
        this.showComponentDeletionModal = true;
    }

    onToggleVisibility(selectedComponent: NoteComponentViewState, componentIndex: number): void {
        const isHidden = !selectedComponent.isHidden;

        this.componentData[componentIndex].isHidden = isHidden;

        const updatedComponent: INotesComponent = {
            ...this.componentData[componentIndex],
            data: this.dataToBeSaved[componentIndex],
            isHidden,
        };

        this.onComponentDataChange.emit({ data: updatedComponent, componentIndex });
    }

    OnDeleteComponent(): void {
        this.componentData.splice(this.componentIndexToBeDeleted, 1);
        this.dataToBeSaved.splice(this.componentIndexToBeDeleted, 1);
        this.onDeleteComponent.emit(this.componentIndexToBeDeleted);
        this.resetDeleteComponentData();
    }

    OnCancelDeleteComponent(): void {
        this.resetDeleteComponentData();
    }

    private resetDeleteComponentData(): void {
        this.componentIndexToBeDeleted = null;
        this.showComponentDeletionModal = false;
    }

    onSave(selectedComponentViewModel: NoteComponentViewState, componentIndex: number): void {
        if (this.dataToBeSaved[componentIndex] != null) {
            selectedComponentViewModel.data = this.dataToBeSaved[componentIndex];
        }

        /** Remove state from object before sending it to backend */
        selectedComponentViewModel.state = undefined;

        selectedComponentViewModel.isSaveButtonClicked = true;

        this.componentData[componentIndex] = selectedComponentViewModel;

        const data: INotesComponent = {
            data: selectedComponentViewModel.data,
            componentId: selectedComponentViewModel.componentId,
            componentName: selectedComponentViewModel.componentName,
            referenceFlis: selectedComponentViewModel.referenceFlis,
            addNumbering: selectedComponentViewModel.addNumbering,
            hasFootnotes: selectedComponentViewModel.hasFootnotes,
            isHidden: selectedComponentViewModel.isHidden,
        };
        if (selectedComponentViewModel.componentTitle != null) {
            data.componentTitle = selectedComponentViewModel.componentTitle;
        }
        this.onComponentDataChange.emit({ data: data, componentIndex });
    }

    drop(event: CdkDragDrop<Notes[]>): void {
        const { previousIndex, currentIndex } = event;
        moveItemInArray(this.componentData, previousIndex, currentIndex);
        moveItemInArray(this.dataToBeSaved, previousIndex, currentIndex);

        this.onDropComponents.emit({ previousIndex, currentIndex });
    }

    private addSvgIcons(): void {
        this.matIconRegistry.addSvgIcon(
            'aumico-icon-pattern-drag-component',
            this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/icons/aumico-icon-pattern-drag-component.svg'),
        );
    }

    // Function to trigger reset for a specific component
    resetChildComponent(componentIndex: number) {
        this.resetState[componentIndex] = true;

        this.recheckReferencesExistenceForComponent(this.componentData[componentIndex], componentIndex);

        setTimeout(() => (this.resetState[componentIndex] = false), 0);
    }

    public closeFlyoutAddMenu(): void {
        this.isMenuOpenIndex = null;
        this.positionCalculated = false;
        this.componentIndexToInsertAfter = undefined;
    }

    public toggleFlyoutAddMenu(event: MouseEvent, accordionIndex: number, componentIndex: number): void {
        event.stopPropagation();
        this.isMenuOpenIndex = this.isMenuOpenIndex === accordionIndex ? null : accordionIndex;
        this.positionCalculated = false;

        const buttonElement = event.target as HTMLElement;
        const rect = buttonElement.getBoundingClientRect();

        // Initial position directly below the button
        const right = window.innerWidth - rect.right - rect.width;
        const top = rect.bottom + rect.height;
        this.menuPosition = { top, right };
        this.componentIndexToInsertAfter = componentIndex;
    }

    public addNewComponent(componentType: ComponentType): void {
        this.isMenuOpenIndex = null;
        const noteComponentData = this.notesHelper.createEmptyNoteComponent(
            componentType,
            this.annualReportDefaultSettings,
            this.languages,
            this.fxNotesContext != null,
        );
        const componentId = NotesHelper.generateComponentId(componentType);

        const newComponent: INotesComponent = {
            componentId,
            componentName: componentType,
            data: noteComponentData,
            hasFootnotes: false,
            isHidden: false,
        };

        this.insertComponentInComponentData(newComponent);
    }

    onPasteComponent(aumicoClipboardData: AumicoClipboardData): void {
        this.isMenuOpenIndex = null;

        if (aumicoClipboardData == null || aumicoClipboardData.component == null) {
            return;
        }

        let notesComponent: INotesComponent | undefined;
        const balanceFlis = getAllFlis(this._balanceMappingTemplate.group);
        const incomeFlis = getAllFlis(this._incomeMappingTemplate.group);
        const allFlis = balanceFlis.concat(incomeFlis);

        if (aumicoClipboardData.componentType === ComponentType.SUPER_TABLE) {
            notesComponent = this.notesHelper.copySuperTableComponentStructureWithData(
                aumicoClipboardData.component,
                allFlis,
                this._balanceMappingTemplate,
                this._incomeMappingTemplate,
                false,
            );
        }
        if (aumicoClipboardData.componentType === ComponentType.FREE_TABLE_WITH_AMOUNTS) {
            notesComponent = this.notesHelper.copyFreeTableWithAmountsComponentStructureWithData(
                aumicoClipboardData.component,
                allFlis,
                false,
            );
        }

        this.insertComponentInComponentData(notesComponent);

        this.aumicoCoreProvider.dispatchAddNotification(
            undefined,
            'aumico_notes_component_pasted_successfully',
            NotificationType.SUCCESS,
            2000,
        );
    }

    private insertComponentInComponentData(notesComponent: INotesComponent): void {
        // Compute insertion index = after the given componentIndex
        // Clamp to [0, this.componentData.length] to avoid out-of-range issues
        const afterIdx = Number.isInteger(this.componentIndexToInsertAfter)
            ? this.componentIndexToInsertAfter
            : this.componentData.length - 1;
        const insertIndex = Math.min(Math.max(afterIdx + 1, 0), this.componentData.length);

        // Insert the new component
        this.componentData.splice(insertIndex, 0, notesComponent);

        // Track to-be-saved data
        this.dataToBeSaved.splice(insertIndex, 0, notesComponent.data);

        // Emit change for the inserted item
        this.onComponentDataChange.emit({ data: this.componentData[insertIndex], componentIndex: insertIndex });

        // Put the inserted component into editing state
        (this.componentData[insertIndex] as any).state = ViewState.EDITING;
    }

    ngAfterViewChecked() {
        // Recalculate position only when the menu is open and not yet calculated
        if (this.isMenuOpenIndex !== null && !this.positionCalculated && this.menuFlyout) {
            this.adjustPositionToFitViewport();
            this.positionCalculated = true; // Ensure it only runs once per toggle
        }
    }

    private adjustPositionToFitViewport() {
        const flyoutRect = this.menuFlyout.nativeElement.getBoundingClientRect();

        let { top, right } = this.menuPosition;

        if (flyoutRect.bottom > window.innerHeight) {
            top = window.innerHeight - flyoutRect.height - 10;
        }

        if (flyoutRect.right > window.innerWidth) {
            right = 10;
        }

        this.menuPosition = { top, right };
    }

    onTransposeButtonClick(selectedComponent: NoteComponentViewState, componentIndex: number) {
        if (
            !selectedComponent.data.options.transpose &&
            this.componentData[componentIndex].componentName === ComponentType.SUPER_TABLE
        ) {
            if (
                (this.dataToBeSaved[componentIndex] as ISuperTableComponent).rows.length <= MAX_SUPER_TABLE_ROW_NUMBER
            ) {
                this.onComponentOptionsBooleanPropertyChange(selectedComponent, 'transpose', true, componentIndex);
            } else {
                this.dialog.open<ModalComponent, ModalData>(ModalComponent, {
                    disableClose: false,
                    panelClass: 'custom-modalbox',
                    data: {
                        showCloseButton: true,
                        titleIcon: ModalTitleIcons.WARNING,
                        content: {
                            text: ['aumico_notes_super_table_turning_on_transpose_toggle_not_allowed'],
                            icon: 'document-information',
                        },
                    },
                });
            }

            return;
        }

        if (
            selectedComponent.data.options.transpose &&
            (this.componentData[componentIndex].data as ISuperTableComponent).options.xAxisConfig.length <=
            MAX_SUPER_TABLE_COLUMN_NUMBER
        ) {
            this.onComponentOptionsBooleanPropertyChange(selectedComponent, 'transpose', false, componentIndex);
        } else {
            this.dialog.open<ModalComponent, ModalData>(ModalComponent, {
                disableClose: false,
                panelClass: 'custom-modalbox',
                data: {
                    showCloseButton: true,
                    titleIcon: ModalTitleIcons.WARNING,
                    content: {
                        text: ['aumico_notes_super_table_turning_off_transpose_toggle_not_allowed'],
                        icon: 'document-information',
                    },
                },
            });
        }
    }

    onFlipSignsButtonClick(selectedComponent: NoteComponentViewState, componentIndex: number) {
        this.onComponentOptionsBooleanPropertyChange(
            selectedComponent,
            'flipSign',
            !selectedComponent.data.options.flipSign,
            componentIndex,
        );
    }

    onAdd(selectedComponent: NoteComponentViewState, componentIndex: number) {
        const dynamicComponent = this.dynamicComponents.get(componentIndex);
        if (dynamicComponent && 'addItem' in dynamicComponent && typeof dynamicComponent['addItem'] === 'function') {
            dynamicComponent.addItem();
        }
    }

    onTotalSignsButtonClick(selectedComponent: NoteComponentViewState, componentIndex: number) {
        this.onComponentOptionsBooleanPropertyChange(
            selectedComponent,
            'totalRow',
            !selectedComponent.data.options.totalRow,
            componentIndex,
        );

        const dynamicComponent = this.dynamicComponents.get(componentIndex);
        if (
            dynamicComponent &&
            'toggleTotalRow' in dynamicComponent &&
            typeof dynamicComponent['toggleTotalRow'] === 'function'
        ) {
            dynamicComponent.toggleTotalRow();
        }
    }

    onCompareYearsButtonClick(selectedComponent: NoteComponentViewState, componentIndex: number) {
        if (selectedComponent.data.options.compareYears === true) {
            this.showCompareYearsDeletionDialog = true;
            this.selectedComponentForCompareYearsDeletion = selectedComponent;
            this.componentIndexForCompareYearsDeletion = componentIndex;
        } else {
            this.onComponentOptionsBooleanPropertyChange(
                selectedComponent,
                'compareYears',
                !selectedComponent.data.options.compareYears,
                componentIndex,
            );
        }
    }

    /**
     * FX (REV-21512): hide/show the display-currency twin columns — same plumbing as
     * compareYears/hideCurrentYear, but without a confirm dialog: twins carry no data.
     */
    onFxHideCurrentYearButtonClick(selectedComponent: NoteComponentViewState, componentIndex: number): void {
        this.onComponentOptionsBooleanPropertyChange(
            selectedComponent,
            'fxHideCurrentYear',
            selectedComponent.data.options.fxHideCurrentYear !== true,
            componentIndex,
        );
    }

    onFxHidePreviousYearButtonClick(selectedComponent: NoteComponentViewState, componentIndex: number): void {
        this.onComponentOptionsBooleanPropertyChange(
            selectedComponent,
            'fxHidePreviousYear',
            selectedComponent.data.options.fxHidePreviousYear !== true,
            componentIndex,
        );
    }

    onHideCurrentYearButtonClick(selectedComponent: NoteComponentViewState, componentIndex: number) {
        if (selectedComponent.data.options.hideCurrentYear === false) {
            this.showCurrentYearDeletionDialog = true;
            this.selectedComponentForCurrentYearDeletion = selectedComponent;
            this.componentIndexForCurrentYearDeletion = componentIndex;
        } else {
            this.onComponentOptionsBooleanPropertyChange(
                selectedComponent,
                'hideCurrentYear',
                !selectedComponent.data.options.hideCurrentYear,
                componentIndex,
            );
        }
    }

    onDeleteCompareYearsColumn(): void {
        this.onComponentOptionsBooleanPropertyChange(
            this.selectedComponentForCompareYearsDeletion,
            'compareYears',
            !this.selectedComponentForCompareYearsDeletion.data.options.compareYears,
            this.componentIndexForCompareYearsDeletion,
        );

        this.showCompareYearsDeletionDialog = false;
    }
    onDeleteCurrentYearColumn(): void {
        this.onComponentOptionsBooleanPropertyChange(
            this.selectedComponentForCurrentYearDeletion,
            'hideCurrentYear',
            !this.selectedComponentForCurrentYearDeletion.data.options.hideCurrentYear,
            this.componentIndexForCurrentYearDeletion,
        );

        this.showCurrentYearDeletionDialog = false;
    }

    onCancelDeleteCompareYears(): void {
        this.showCompareYearsDeletionDialog = false;
        this.selectedComponentForCompareYearsDeletion = undefined;
        this.componentIndexForCompareYearsDeletion = undefined;
    }
    onCancelDeleteCurrentYear(): void {
        this.showCurrentYearDeletionDialog = false;
        this.selectedComponentForCurrentYearDeletion = undefined;
        this.componentIndexForCurrentYearDeletion = undefined;
    }

    onShowPreviousYearButtonClick(selectedComponent: NoteComponentViewState, componentIndex: number) {
        this.onComponentOptionsBooleanPropertyChange(
            selectedComponent,
            'showPreviousYear',
            !selectedComponent.data.options.showPreviousYear,
            componentIndex,
        );
    }

    onShowBalanceSheetDateButtonClick(selectedComponent: NoteComponentViewState, componentIndex: number) {
        this.onComponentOptionsBooleanPropertyChange(selectedComponent, 'showFiscalYear', false, componentIndex);
    }

    onShowFiscalYearButtonClick(selectedComponent: NoteComponentViewState, componentIndex: number) {
        this.onComponentOptionsBooleanPropertyChange(selectedComponent, 'showFiscalYear', true, componentIndex);
    }

    onShowCurrencyButtonClick(selectedComponent: NoteComponentViewState, componentIndex: number, value: boolean) {
        this.onComponentOptionsBooleanPropertyChange(
            selectedComponent,
            'showCurrency',
            value,
            componentIndex,
        );
    }

    onPageBreakButtonClick(selectedComponent: NoteComponentViewState, componentIndex: number) {
        this.onComponentOptionsBooleanPropertyChange(
            selectedComponent,
            'pageBreak',
            !selectedComponent.data.options.pageBreak,
            componentIndex,
        );
    }

    async onCopyComponent(selectedComponent: NoteComponentViewState, componentIndex: number): Promise<void> {
        const component = omit(selectedComponent, ['state', 'isSaveButtonClicked']);
        // The component data might have been edited and not saved yet, so we need to ensure we copy the latest data
        const editedComponentData = this.dataToBeSaved[componentIndex];
        if (editedComponentData) {
            component.data = editedComponentData;
        }

        await this.aumicoClipboardService.copyComponentToClipboard(component.componentName, component);

        this.aumicoCoreProvider.dispatchAddNotification(
            undefined,
            'aumico_notes_component_copied_to_clipboard',
            NotificationType.SUCCESS,
            2000,
        );
    }

    onFormStatusChange(isValid: boolean, componentIndex: number): void {
        this.formStatus[componentIndex] = !isValid;
    }

    isComponentEmpty(component: NoteComponentViewState): boolean {
        if (
            component.componentName === ComponentType.FINANCIAL_LINE_ITEM ||
            component.componentName === ComponentType.RICH_TEXT ||
            component.componentName === ComponentType.MOVEMENT_SCHEDULE
        ) {
            return !this.accordionCompletenessHelper.checkCompletenessOfComponents(
                component.componentName,
                component.data,
                this.reportLocale,
            );
        }

        return false;
    }

    onOpenTableReferenceDialog(selectedComponent: NoteComponentViewState, componentIndex: number): void {
        const refDialog = this.dialog.open(ReferencingNotesComponent, {
            data: {
                flis: this.flisWithAccounts,
                selectedFlis: this.dataToBeSaved[componentIndex].referenceFlis,
            } as ReferencingNotesComponentData,
            width: '1000px',
            height: '600px',
            panelClass: 'notes-reference-container',
        });

        refDialog
            .afterClosed()
            .pipe(untilDestroyed(this))
            .subscribe((result: IFLIForNotes[]) => {
                if (!result) return;

                const referenceFlisIds = result.map((fli) => fli.id);
                this.dataToBeSaved[componentIndex].referenceFlis = referenceFlisIds;

                if (referenceFlisIds.length > EMPTY_ARRAY_LENGTH) {
                    this.dataToBeSaved[componentIndex].addNumbering = true;
                }

                this.componentData[componentIndex].data = this.dataToBeSaved[componentIndex];
                this.componentData[componentIndex] = {
                    ...this.componentData[componentIndex]
                };

                const dataToBeEmitted: Pick<Notes, 'addNumbering' | 'referenceFlis'> = {
                    referenceFlis: this.dataToBeSaved[componentIndex].referenceFlis,
                    addNumbering:
                        referenceFlisIds.length > EMPTY_ARRAY_LENGTH
                            ? true
                            : this.dataToBeSaved[componentIndex].addNumbering,
                };

                this.onTableReferenceChange.emit({
                    data: dataToBeEmitted,
                    componentIndex,
                });

                this.onComponentDataChange.emit({ data: this.componentData[componentIndex], componentIndex });

            });
    }

    onOpenComponentReferenceDialog(selectedComponent: NoteComponentViewState, componentIndex: number): void {
        const refDialog = this.dialog.open(ReferencingNotesComponent, {
            data: {
                flis: this.flisWithAccounts,
                selectedFlis: selectedComponent.referenceFlis,
            } as ReferencingNotesComponentData,
            width: '1000px',
            height: '600px',
            panelClass: 'notes-reference-container',
        });

        refDialog
            .afterClosed()
            .pipe(untilDestroyed(this))
            .subscribe((result: IFLIForNotes[]) => {
                if (!result) return;

                const referenceFlisIds = result.map((fli) => fli.id);
                selectedComponent.referenceFlis = referenceFlisIds;

                this.componentData[componentIndex].data = this.dataToBeSaved[componentIndex];
                this.componentData[componentIndex] = {
                    ...this.componentData[componentIndex],
                    referenceFlis: selectedComponent.referenceFlis,
                };


                if (referenceFlisIds.length > EMPTY_ARRAY_LENGTH) {
                    selectedComponent.addNumbering = true;
                    this.componentData[componentIndex] = {
                        ...this.componentData[componentIndex],
                        addNumbering: selectedComponent.addNumbering,
                        referenceFlis: selectedComponent.referenceFlis ?? [],
                    };
                }

                const dataToBeEmitted: Pick<NoteComponentViewState, 'addNumbering' | 'referenceFlis'> = {
                    referenceFlis: referenceFlisIds,
                    addNumbering: referenceFlisIds.length > EMPTY_ARRAY_LENGTH ? true : selectedComponent.addNumbering,
                };

                this.onComponentReferenceChange.emit({
                    data: dataToBeEmitted,
                    componentIndex,
                });

                this.onComponentDataChange.emit({ data: this.componentData[componentIndex], componentIndex });

            });
    }

    onAddTableNumberToggleEmit(
        addNumbering: { checked: boolean; tableIndex?: number },
        selectedComponent: NoteComponentViewState,
        componentIndex: number,
    ): void {
        if (
            selectedComponent.componentName === ComponentType.FINANCIAL_LINE_ITEM ||
            selectedComponent.componentName === ComponentType.MOVEMENT_SCHEDULE
        ) {
            this.dataToBeSaved[componentIndex] = {
                ...this.dataToBeSaved[componentIndex],
                flis: [
                    ...this.dataToBeSaved[componentIndex].flis.slice(0, addNumbering.tableIndex),
                    {
                        ...this.dataToBeSaved[componentIndex].flis[addNumbering.tableIndex],
                        addNumbering: addNumbering.checked,
                    },
                    ...this.dataToBeSaved[componentIndex].flis.slice(addNumbering.tableIndex + 1),
                ],
            };
        } else {
            this.dataToBeSaved[componentIndex] = {
                ...this.dataToBeSaved[componentIndex],
                addNumbering: addNumbering.checked,
            };
        }

        this.componentData[componentIndex].data = this.dataToBeSaved[componentIndex];
        this.componentData[componentIndex] = {
            ...this.componentData[componentIndex]
        };
        const dataToBeEmitted: Pick<Notes, 'addNumbering' | 'referenceFlis'> = {
            referenceFlis: this.dataToBeSaved[componentIndex].referenceFlis,
            addNumbering: addNumbering.checked,
        };

        this.onTableReferenceChange.emit({
            data: dataToBeEmitted,
            componentIndex,
            tableIndex: addNumbering.tableIndex,
        });

        this.onComponentDataChange.emit({ data: this.componentData[componentIndex], componentIndex });

    }

    private recheckReferencesExistenceForAllComponents(): void {
        let componentIndex = -1;
        for (const component of this.componentData) {
            componentIndex = componentIndex + 1;
            this.recheckReferencesExistenceForComponent(component, componentIndex);
        }
    }

    private recheckReferencesExistenceForComponent(component: NoteComponentViewState, componentIndex: number): void {
        const allFliIds = this.flisWithAccounts.map((fli) => fli.id);

        // Check References Inside Component Data
        if (
            component.data != null &&
            component.data.referenceFlis != null &&
            component.data.referenceFlis.length !== EMPTY_ARRAY_LENGTH
        ) {
            const existingComponentDataReferences = component.data.referenceFlis.filter((fliId) =>
                allFliIds.includes(fliId),
            );

            setTimeout(() => {
                this.dataToBeSaved[componentIndex] = {
                    ...this.dataToBeSaved[componentIndex],
                    referenceFlis: existingComponentDataReferences,
                };
            });

            const componentDataToBeEmitted: Pick<Notes, 'addNumbering' | 'referenceFlis'> = {
                referenceFlis: existingComponentDataReferences,
                addNumbering: this.dataToBeSaved[componentIndex].addNumbering,
            };

            this.onTableReferenceChange.emit({
                data: componentDataToBeEmitted,
                componentIndex,
            });
        }

        // Check References on Component Titles

        if (component.referenceFlis == null || component.referenceFlis.length === EMPTY_ARRAY_LENGTH) {
            return;
        }

        const existingReferences = component.referenceFlis.filter((fliId) => allFliIds.includes(fliId));
        component.referenceFlis = existingReferences;

        const dataToBeEmitted: Pick<NoteComponentViewState, 'addNumbering' | 'referenceFlis'> = {
            referenceFlis: existingReferences,
            addNumbering: component.addNumbering,
        };

        this.onComponentReferenceChange.emit({
            data: dataToBeEmitted,
            componentIndex,
        });
    }

    onSettingsToggle(selectedComponent, componentIndex: number) {
        // Check if toggleSettings exists and is a function
        const component = this.dynamicComponents.get(componentIndex);
        if ('toggleSettings' in component && typeof component['toggleSettings'] === 'function') {
            component.toggleSettings();
        }
    }

    onToggleFootnote(selectedComponent, componentIndex: number) {
        // Check if toggleFootnote exists and is a function
        const component = this.dynamicComponents.get(componentIndex);
        const newValue = !selectedComponent.hasFootnotes;
        selectedComponent.hasFootnotes = newValue;
        if ('hasFootnotes' in component) {
            component.hasFootnotes = newValue;
        }

        // Update the component data and emit the change
        if (this.componentData[componentIndex] != null) {
            const updatedComponent: INotesComponent = {
                ...this.componentData[componentIndex],
                data: {
                    ...this.dataToBeSaved[componentIndex],
                    hasFootnotes: newValue,
                },
                hasFootnotes: newValue,
            };

            this.onComponentDataChange.emit({ data: updatedComponent, componentIndex });
        }
    }

    onSettingsClose(value: boolean, componentIndex: number) {
        // Check if toggleSettings exists and is a function
        try {
            const component = this.componentData[componentIndex];
            component.data.options.settings = value;
            const dynamicNotesActionsPalette = this.dynamicNotesActionsPalette.get(componentIndex);
            dynamicNotesActionsPalette.settings = value;
        } catch (error) {
            console.error('Error in closing settings', error);
        }
    }

    onAddComponentNumberToggleEmit(selectedComponent: NoteComponentViewState, componentIndex: number): void {
        selectedComponent.addNumbering = !selectedComponent.addNumbering;


        this.componentData[componentIndex].data = this.dataToBeSaved[componentIndex];
        this.componentData[componentIndex] = {
            ...this.componentData[componentIndex],
            addNumbering: selectedComponent.addNumbering,
        };

        const dataToBeEmitted: Pick<NoteComponentViewState, 'addNumbering' | 'referenceFlis'> = {
            referenceFlis: selectedComponent.referenceFlis,
            addNumbering: selectedComponent.addNumbering,
        };

        this.onComponentReferenceChange.emit({
            data: dataToBeEmitted,
            componentIndex,
        });

        this.onComponentDataChange.emit({ data: this.componentData[componentIndex], componentIndex });


    }

    onDropFli(previousIndex: number, currentIndex: number, componentIndex: number): void {
        moveItemInArray(this.componentData[componentIndex].data.flis, previousIndex, currentIndex);
        this.dataToBeSaved[componentIndex] = this.componentData[componentIndex].data;
        const updatedComponent: INotesComponent = {
            ...this.componentData[componentIndex],
            data: this.dataToBeSaved[componentIndex],
        };

        this.onComponentDataChange.emit({ data: updatedComponent, componentIndex });
    }

    onComponentTitleChange(newTitle: string, component: NoteComponentViewState, componentIndex: number): void {
        if (this.dataToBeSaved[componentIndex] != null) {
            component.data = this.dataToBeSaved[componentIndex];
        }

        if (component.componentTitle == null) {
            component.componentTitle = {
                [this.reportLocale]: newTitle,
            };
        } else {
            component.componentTitle[this.reportLocale] = newTitle;
        }

        this.componentData[componentIndex] = component;

        const data: INotesComponent = {
            data: component.data,
            componentId: component.componentId,
            componentName: component.componentName,
            referenceFlis: component.referenceFlis,
            addNumbering: component.addNumbering,
            componentTitle: component.componentTitle,
            hasFootnotes: component.hasFootnotes,
        };
        this.onComponentDataChange.emit({ data: data, componentIndex });
    }

    /**
     * FX (REV-21512): the 3-state currency chip on FLI components. The state lives in
     * `IFinancialLineItemOptions.currencyDisplay` (blob, no migration) and copies with the
     * master report (G1); absent means `both` on FX reports.
     */
    cycleFliCurrencyDisplay(component: NoteComponentViewState, componentIndex: number): void {
        const current = this.effectiveFliCurrencyDisplay(component);
        const next = current === 'both' ? 'display' : current === 'display' ? 'functional' : 'both';
        if (this.dataToBeSaved[componentIndex] != null) {
            component.data = this.dataToBeSaved[componentIndex];
        }
        component.data.options.currencyDisplay = next;
        this.componentData[componentIndex] = component;
        const data: INotesComponent = {
            data: component.data,
            componentId: component.componentId,
            componentName: component.componentName,
            referenceFlis: component.referenceFlis,
            addNumbering: component.addNumbering,
            componentTitle: component.componentTitle,
            hasFootnotes: component.hasFootnotes,
        };
        this.onComponentDataChange.emit({ data: data, componentIndex });
    }

    fliCurrencyChipLabel(component: NoteComponentViewState): string {
        const functional = this.annualReportDefaultSettings?.currency ?? '';
        const display = this.annualReportDefaultSettings?.displayCurrency ?? this.fxNotesContext?.displayCurrency ?? '';
        const state = this.effectiveFliCurrencyDisplay(component);

        return state === 'both' ? `${functional} & ${display}` : state === 'display' ? display : functional;
    }

    effectiveFliCurrencyDisplay(component: NoteComponentViewState): FxCurrencyDisplay {
        return effectiveFxCurrencyDisplay(component.data?.options, this.fxNotesContext != null);
    }

    private onComponentOptionsBooleanPropertyChange(
        component: NoteComponentViewState,
        key: string,
        value: boolean,
        componentIndex: number,
    ) {
        if (this.dataToBeSaved[componentIndex] != null) {
            component.data = this.dataToBeSaved[componentIndex];
        }

        component.data.options[key] = value;

        this.componentData[componentIndex] = component;

        const data: INotesComponent = {
            data: component.data,
            componentId: component.componentId,
            componentName: component.componentName,
            referenceFlis: component.referenceFlis,
            addNumbering: component.addNumbering,
            componentTitle: component.componentTitle,
            hasFootnotes: component.hasFootnotes,
        };
        this.onComponentDataChange.emit({ data: data, componentIndex });
    }

    private initializeDataToBeSaved(noteComponents: INotesComponent[]): void {
        this.dataToBeSaved = [];
        noteComponents.forEach((component) => {
            this.dataToBeSaved.push(cloneDeep<Notes>(component.data));
        });
    }
}
